
// For types that need a runtime list of their values, the list is defined here `as const`,
// and the type (and type guard, if needed) are derived from it, so the list is the single source of truth.
// Constants.ts builds its lists (or filtered versions of them) from these.

const makeTypeGuard = <T extends string | number>(values: readonly T[]) =>
  (value: string | number): value is T => (values as readonly (string | number)[]).includes(value);

export const ABILITY_SCORE_TYPE_VALUES = ["STR","DEX","CON","INT","WIS","CHA"] as const;
export type AbilityScoreType = typeof ABILITY_SCORE_TYPE_VALUES[number];
export const isStatType = makeTypeGuard(ABILITY_SCORE_TYPE_VALUES);

export const ROLL_TYPE_VALUES = ["Attack","Saving Throw","Ability Check","Skill Check"] as const;
export type RollType = typeof ROLL_TYPE_VALUES[number];

export const ABILITY_SCORE_EXPANDED_TYPE_VALUES = [
  ...ABILITY_SCORE_TYPE_VALUES,"Spellcasting","Same as Feat","Selectable","STR or DEX","INT, WIS, or CHA","Your Choice","None","Attack"
] as const;
export type AbilityScoreExpandedType = typeof ABILITY_SCORE_EXPANDED_TYPE_VALUES[number];
export const isStatExpandedType = makeTypeGuard(ABILITY_SCORE_EXPANDED_TYPE_VALUES);

export type DefenseType = AbilityScoreType | "Physical" | "Mental" | "Spiritual";
export type ActionType = "Attack" | "Ability" | "Magic" | "Movement" | "Item Action" | "None" | "Inherit";

export const ATTACK_TYPE_VALUES = ["Melee","Ranged","Melee Spell","Ranged Spell","Bonus","Save","Thrown","Light","Versatile","None"] as const;
export type AttackType = typeof ATTACK_TYPE_VALUES[number];
export const isAttackType = makeTypeGuard(ATTACK_TYPE_VALUES);

export type RangeType = "Touch" | "Self" | "Unlimited" | "Spaces";

// 0 = Not Set, 1 = Tiny, 2 = Small, 3 = Medium, 4 = Large, 5 = Huge, 6 = Gargantuan, 7 || 8 = Size set in other location
export const SIZE_TYPE_VALUES = [0,1,2,3,4,5,6,7,8] as const;
export type SizeType = typeof SIZE_TYPE_VALUES[number];
export const isSizeType = makeTypeGuard(SIZE_TYPE_VALUES);

export type SpecialCastingTime = "Reaction" | "Mini Rest" | "Short Rest" | "Long Rest" | "Down Time" | "Initiative" | "Round" | "None";

export const REFRESH_RULE_TYPE_VALUES = [
  "Short Rest",
  "Long Rest",
  "Dawn",
  "1 Hour",
  "2 Hours",
  "8 Hours",
  "24 Hours",
  "Special",
  "None"
] as const;
export type RefreshRuleType = typeof REFRESH_RULE_TYPE_VALUES[number];

export type KnowledgeType = "Adaptation" | "Transform" | "Skill" | "Tool" | "Language" | "Spell" | "Creature" | "Crafting Recipe" | "None";

export const CREATURE_TYPE_VALUES = [
  "Humanoid",
  "Beast",
  "Construct",
  "Aberration",
  "Undead",
  "Dragon",
  "Monstrosity",
  "Elemental",
  "Fey",
  "Giant",
  "Ooze",
  "Plant",
  "Shapechanger",
  "Celestial",
  "Fiend",
  "None"
] as const;
export type CreatureType = typeof CREATURE_TYPE_VALUES[number];
export const isCreatureType = makeTypeGuard(CREATURE_TYPE_VALUES);

export const ITEM_TYPE_VALUES = [
  "Armor",
  "Weapon",
  "Ammunition",
  "Consumable",
  "Artisan's Tools",
  "Gaming Set",
  "Musical Instrument",
  "Potion",
  "Poison",
  "Dust",
  "Ring",
  "Rod",
  "Scroll",
  "Staff",
  "Tool",
  "Vehicle",
  "Wand",
  "Wondrous",
  "Shoe",
  "Cape/Cloak",
  "Gloves",
  "Headgear",
  "Necklace",
  "Belt",
  "Manual",
  "Container",
  "Bracers",
  "Gem",
  "Holy Symbol",
  "Arcane Focus",
  "Druidic Focus",
  "Other",
  "Custom",
  "Deck",
  "Card",
  "Book",
  // "Prosthesis"
] as const;
export type ItemType = typeof ITEM_TYPE_VALUES[number];
export const isItemType = makeTypeGuard(ITEM_TYPE_VALUES);

export const WEAPON_TYPE_VALUES = [
  "Battleaxe","Blowgun","Club","Crossbow, Hand",
  "Crossbow, Heavy","Crossbow, Light","Dagger",
  "Dart","Flail","Glaive",
  "Greataxe","Greatclub","Greatsword",
  "Halberd","Handaxe","Javelin","Lance",
  "Light Hammer","Longbow","Longsword",
  "Mace","Maul","Morningstar","Pike","Quarterstaff",
  "Rapier","Scimitar","Shortbow","Shortsword",
  "Sickle","Sling","Spear","Trident",
  "War Pick","Warhammer","Whip","None"
] as const;
export type WeaponType = typeof WEAPON_TYPE_VALUES[number];

export const AMMUNITION_TYPE_VALUES = ["Arrow","Crossbow Bolt","Sling Bullet","Bullet","Stone","Needle","Special","Ōdzutsu Shot","None"] as const;
export type AmmunitionType = typeof AMMUNITION_TYPE_VALUES[number];

export const BOOK_TYPE_VALUES = ["Spell","Creature","Item","Other"] as const;
export type BookType = typeof BOOK_TYPE_VALUES[number];

export const CASTING_TIME_VALUES = [
  "A",
  "BA",
  "RA",
  "Attack",
  "Short Rest",
  "Long Rest",
  "Initiative",
  "Start of Turn",
  "End of Turn",
  "Passive",
  "On Struck",
  "1 Minute",
  "10 Minutes",
  "1 Hour",
  "Special",
] as const;
export type CastingTime = typeof CASTING_TIME_VALUES[number];
export type CastingTimeWithStandardSpecials = CastingTime | "1 Minute" | "10 Minutes" | "1 Hour";

export const TARGET_TYPE_VALUES = ["X Targets","Area","Self","None"] as const;
export type TargetType = typeof TARGET_TYPE_VALUES[number];

export const AREA_TYPE_VALUES = ["Cone","Line","Cube","Square","Sphere","Circle","Cylinder","Wall","None"] as const;
export type AreaType = typeof AREA_TYPE_VALUES[number];

export const SPELL_LEVEL_VALUES = [-1,0,1,2,3,4,5,6,7,8,9] as const;
export type SpellLevel = typeof SPELL_LEVEL_VALUES[number];
export const isSpellLevel = makeTypeGuard(SPELL_LEVEL_VALUES);

export const SPELL_LEVEL_STRING_VALUES = ["Cantrip","1","2","3","4","5","6","7","8","9"] as const;
export type SpellLevelString = typeof SPELL_LEVEL_STRING_VALUES[number];

export const SCHOOL_OF_MAGIC_VALUES = [
  "Conjuration",
  "Necromancy",
  "Evocation",
  "Abjuration",
  "Transmutation",
  "Divination",
  "Enchantment",
  "Illusion"
] as const;
export type SchoolOfMagic = typeof SCHOOL_OF_MAGIC_VALUES[number];
export const isSchoolOfMagic = makeTypeGuard(SCHOOL_OF_MAGIC_VALUES);

export const SELECTABLE_TYPE_VALUES = [
  "Ability Score",
  "Damage Type",
  "School of Magic",
  "Dragon Type", // Metallic, Chromatic, Gem
  "Dragon Breed", // Metal, Color, Gem
  "Strings",
  "Spell List",
  "Race",
  "Item",
  "Weapon Type",
  "Spell",
  "Character Spell",
  "Number",
  "Dice Size"
] as const;
export type SelectableType = typeof SELECTABLE_TYPE_VALUES[number];

export const DAMAGE_TYPE_VALUES = [
  "Bludgeoning",
  "Piercing",
  "Slashing",
  "Acid",
  "Cold",
  "Fire",
  "Force",
  "Lightning",
  "Necrotic",
  "Poison",
  "Psychic",
  "Radiant",
  "Thunder",
  "Typeless",
  "Same as Main"
] as const;
export type DamageType = typeof DAMAGE_TYPE_VALUES[number];
export const isDamageType = makeTypeGuard(DAMAGE_TYPE_VALUES);

// Kinds of Crafting Materials that can be looted from creatures. More will be added later.
export const CRAFTING_MATERIAL_TYPE_VALUES = ["Magical Essence","Flesh","Hide","Bone"] as const;
export type CraftingMaterialType = typeof CRAFTING_MATERIAL_TYPE_VALUES[number];
export const isCraftingMaterialType = makeTypeGuard(CRAFTING_MATERIAL_TYPE_VALUES);

export const EFFECT_TYPE_VALUES = [
  "None","Control","Summon","Teleport","Portal","Transform","Adaptation",
  "Create Resource","Utility","Create Item","Enhance Item","Enhance Creature","Healing",
  "Max HP","Temp HP","Bonus Roll",
  "Self Condition","Self Gift","Special","Selectable Key",
  "Rollable Table","Use Extra","Draw Card",
  "Damage","Special Feature"
] as const;
export type EffectType = typeof EFFECT_TYPE_VALUES[number]; // DamageType;

export const DURATION_TYPE_VALUES = [
  "Instantaneous",
  "1 Round",
  "2 Rounds",
  "1 Minute",
  "10 Minutes",
  "1 Hour",
  "8 Hours",
  "24 Hours",
  "Until Dispelled",
  "Special"
] as const;
export type DurationType = typeof DURATION_TYPE_VALUES[number];

export const POTENCE_TYPE_VALUES = ["Slot","Character","Source","Class","None"] as const;
export type PotenceType = typeof POTENCE_TYPE_VALUES[number];
export const isPotenceType = makeTypeGuard(POTENCE_TYPE_VALUES);

export const MODIFIER_TARGET_VALUES = [
  "AC",
  "Med Armor Max AC from DEX",
  "Max HP",
  "Speed",
  "Ability Check","Skill Check",
  "Saving Throw",
  "Ability Score",
  "Ability Score Max",
  "Spell DC",
  "Attack","Damage","Range",
  "Initiative",
  "Attunement Slots",
  "Table Value",
  "Formula Value",
  "Passive Perception",
  "Passive Insight",
  "Passive Investigation",
  "Size",
  "Size Bonus For Carrying",
  "Carrying Capacity",
  "Max Prepared Spells"
] as const;
export type ModifierTarget = typeof MODIFIER_TARGET_VALUES[number];

export const SPEED_TYPE_VALUES = ["walk","fly","swim","climb","burrow"] as const;
export type SpeedType = typeof SPEED_TYPE_VALUES[number];
export const isSpeedType = makeTypeGuard(SPEED_TYPE_VALUES);

export const CREATURE_FB_TYPE_VALUES = ["Traits","Actions","Legendary Actions","Lair Actions","Bonus Actions","Reactions"] as const;
export type CreatureFBType = typeof CREATURE_FB_TYPE_VALUES[number];
export const isCreatureFBType = makeTypeGuard(CREATURE_FB_TYPE_VALUES);

export const RECIPE_COMPONENT_TYPE_VALUES = ["Any","Animus","Ash","Fluid","Organ","Hair","Bone","Hide","Natural Weapon","Trophy","Other","None"] as const;
export type RecipeComponentType = typeof RECIPE_COMPONENT_TYPE_VALUES[number];

export const CHARACTER_LIMITATION_TYPE_VALUES = [
  "game_class",
  "subclass",
  "class_group",
  "class_or_class_group",
  "race",
  "subrace",
  "gift",
  "feat",
  "alignment",
  "min_ability_score",
  "spellcaster",
  "spells_of_schools",
  "spell",
  "proficiency",
  "at_least_1_weapon",
  "at_least_1_language",
  "level",
  "pact_boon",
  "eldritch_invocation",
  "tags",
  "dragonmark",
  "wild_talent",
  "feature_of_type",
  ""
] as const;
export type CharacterLimitationType = typeof CHARACTER_LIMITATION_TYPE_VALUES[number];
export const isCharacterLimitationType = makeTypeGuard(CHARACTER_LIMITATION_TYPE_VALUES);

export type ModType = "Add" | "Add if > 0" | "Multiply" | "Multiply if > 0" | "Replace" | "Replace if Greater" | "Add Bonus Rolls";

// export type FeatTier = "General" | "Origin" | "Fighting Style" | "Epic" | "Dragonmark" | "Wild Talent";
export const FEAT_TIER_VALUES = ["General","Origin","Epic"] as const; // These have been moved to tags: ,"Fighting Style","Dragonmark","Wild Talent"
export type FeatTier = typeof FEAT_TIER_VALUES[number];
export const isFeatTier = makeTypeGuard(FEAT_TIER_VALUES);

export const TIME_RESTRICTION_VALUES = ["Every Time","1/Turn","Once on Your Turn","Bonus Action","Hide","Special"] as const;
export type TimeRestriction = typeof TIME_RESTRICTION_VALUES[number];

export const HAPPENS_WHEN_VALUES = ["On a Hit","On a Miss","On a Failed Save","On a Succeeded Save","On Attack","On Use","Special"] as const;
export type HappensWhen = typeof HAPPENS_WHEN_VALUES[number];

export type DominanceGroupType = "Race/Species" | "Subrace/Lineage" | "Background" | "Eldritch Invocation" | "Class" | "Subclass" | "Feat" | "Item" | "Spell" | "Other" | "";

export const EXTRA_BASE_TYPE_VALUES = [
  "None",
  "Ability Score",
  "Damage Type",
  "School of Magic",
  "Dragon Type", // Metallic, Chromatic, Gem
  "Dragon Breed", // Metal, Color, Gem
  "Creature Type",
  "Strings",
  "Spell List",
  "Spell"
] as const;
export type ExtraBaseType = typeof EXTRA_BASE_TYPE_VALUES[number];

export const WORLD_SETTING_VALUES = [
  "Forgotten Realms",
  "Eberron",
  "Dragonlance",
  "Ravenloft",
  "Spelljammer",
  "Drakkenheim",
  "Dark Sun",
  "Planescape",
  "Any"
] as const;
export type WorldSetting = typeof WORLD_SETTING_VALUES[number];

export const BASTION_FACILITY_SIZE_VALUES = ["Cramped","Roomy","Vast"] as const;
export type BastionFacilitySize = typeof BASTION_FACILITY_SIZE_VALUES[number];

export const FEATURE_TYPE_VALUES = [
  "Subclass",
  "Subrace",
  "Modifier",
  "Transform Upgrade",
  "Reroll",
  "Spell Modifier",
  "Proficiencies",
  "Expertise",
  "Feat",
  "Selectable",
  "Advantage",
  "Greater Advantage",
  "Damage Multiplier",
  "Resource",
  "Language",
  "Ability Score Improvement",
  "Jack of All Trades",
  "Extra Attacks",
  "Minion Extra Attacks",
  "Ability",
  "Skill as Ability",
  "Skill Ability Override",
  "Creature Ability",
  "Minion Ability",
  "Extra Spell as Ability",
  "Item Affecting Ability", // This is for Pact Weapon, Infusions, and Alchemist Potions
  "Item Affecting Ability Type 2", // This is for Pact Weapon, Infusions, and Alchemist Potions
  "Ritual Casting",
  "Spellcasting",
  "Spell Infusions",
  "Spell Infusion Extra Effects",
  "Spellcasting Focus",
  "Spellbook",
  "Spellbook Modifier",
  "Cantrips", // Increases the max known cantrips for a class
  "Spells", // Increases the max known spells for a class
  "Spells from List",
  "Spells from List with Costs",
  "Bonus Spells",
  "Bonus Spells Upgrade",
  "Extra Spell Lists",
  "Eldritch Invocations",
  "Fighting Style",
  "Pact Boon",
  "Proficiency Override",
  "Ignore Heavy Rule",
  "Specific Special Feature",
  "Special Feature",
  "Special Feature Choices",
  "Special Feature List Merge",
  "Mystic Arcanum",
  "Spell Mastery",
  "Sense",
  "Extra IAA Slots",
  "Condition",
  "Alternate Condition",
  "Allowed Conditions",
  "Start Equipment",
  "Unarmed Strikes",
  "Resource Refresh Rule Change",
  "Replace Ability Score for Attacks",
  "Attack Upgrade", // For SS and GWM, etc
  "Versatile Upgrade",
  "Weapon Mastery",
  "Weapon Mastery Master",
  "Extra Effects", // For Sneak Attack, Flame Tongue, etc
  "Extra Effect Type",
  "Extra Effects of Type",
  "Disable Actions",
  "Bonus Action Attack", // For Crossbow Expert, Dual Wielder, etc
  "Weapon Keyword Upgrade",
  "Weapon Keyword Upgrade 2",
  "Weapon Damage Upgrade", // This is used by Monk Martial Arts for upgrading the damage dice used on weapons
  "Characteristic",
  "Minimum Roll", // For Reliable Talent and Silver Tongue
  "Suggested Spells",
  "Special Feat Access", // Gives access to special types of Feats
  "Light Attack Add Damage Mod",
  "Summon Upgrade",
  "Action Type Upgrade",
  "Knowledge",
  "Learnable",
  "Spell Damage Options",
  "Ignore Stealth Disadvantage",
  "Size Options",
  "Limit Spoof",
  "Special Learnable",
  "Rest Alteration",
  "Creature Type", // Changes your Creature Type
  ""
] as const;
export type FeatureType = typeof FEATURE_TYPE_VALUES[number];

export const SOURCE_BOOK_TYPE_VALUES = [
  "Source Book",
  "Setting",
  "Adventure",
  "Homebrew",
  "Unearthed Arcana"
] as const;
export type SourceBookType = typeof SOURCE_BOOK_TYPE_VALUES[number];
export const isSourceBookType = makeTypeGuard(SOURCE_BOOK_TYPE_VALUES);

export const MAIN_DATA_TYPE_VALUES = [
  "upsertable_base",
  "model_base",
  "feature_source",
  "player_object"
] as const;
export type MainDataType = typeof MAIN_DATA_TYPE_VALUES[number];
export const isMainDataType = makeTypeGuard(MAIN_DATA_TYPE_VALUES);

export const FS_TYPE_VALUES = [
  "feature_source",
  "condition",
  "weapon_keyword",
  "item",
  "magic_item_keyword",
  "feat",
  "special_feature",
  "gift",
  "race",
  "subrace",
  "background",
  "standard_features",
  // "standard_optional_features",
  "sf_special_attributes",
  "game_class",
  "subclass",
  "pact_boon",
  "fighting_style",
  "eldritch_invocation",
  "creature_gift"
] as const;
export type FSType = typeof FS_TYPE_VALUES[number];
export const isFSType = makeTypeGuard(FS_TYPE_VALUES);

export const PLAYER_OBJECT_TYPE_VALUES = [
  "player_object",
  "user_ownership",
  "user_ticket",
  "character",
  "receivable",
  "character_drawn_card",
  "campaign",
  "org_instance",
  "complex_creature",
  "convo_seen_times",
  "conversation"
] as const;
export type PlayerObjectType = typeof PLAYER_OBJECT_TYPE_VALUES[number];
export const isPlayerObjectType = makeTypeGuard(PLAYER_OBJECT_TYPE_VALUES);

export const CAMPAIGN_OBJECT_TYPE_VALUES = [
  "campaign_object",
  "campaign_recipe",
  "campaign_players",
  "campaign_player",
  "campaign_char",
  "campaign_char_options",
  "campaign_deck",
  "campaign_note",
  "campaign_npc",
  "encounter",
  "campaign_quest_board",
  "campaign_inventory",
  "campaign_letter"
] as const;
export type CampaignObjectType = typeof CAMPAIGN_OBJECT_TYPE_VALUES[number];
export const isCampaignObjectType = makeTypeGuard(CAMPAIGN_OBJECT_TYPE_VALUES);

// The campaign object types that have their own list page and can be archived.
export type ArchivableCampaignObjectType =
  "campaign_note" |
  "campaign_npc" |
  "encounter" |
  "campaign_quest_board" |
  "campaign_letter";

export const MISC_DATA_TYPE_VALUES = [
  "user",
  "spell",
  "spell_list",
  "creature",
  "armor_type",
  "table",
  "proficiency",
  "sense",
  "tool",
  "summon",
  "optional_feature_base",
  "floating_feature",
  "creature_feature",
  "reusable_string",
  "template",
  "source_book",
  "sb_section",
  "rollable_table",
  "language",
  "deity",
  "game_deck",
  "game_card",
  "weapon_maneuver",
  "equipment_pack",
  "loot_table",
  "environment",
  "encounter_complication",
  "rule_definition",
  "bastion_facility",
  "ticket",
  "error_log",
  "action_type",
  "skill",
  "creature_group",
  "weapon_mastery_property",
  "spell_slot_type",
  "template_base",
  "feature_user_rating",
  "organization",
  "org_position"
] as const;
export type MiscDataType = typeof MISC_DATA_TYPE_VALUES[number];
export const isMiscDataType = makeTypeGuard(MISC_DATA_TYPE_VALUES);

export type DataType =
  MainDataType |
  FSType |
  PlayerObjectType |
  CampaignObjectType |
  MiscDataType;

export const isDataType = (str: string): str is DataType => {
  return isMainDataType(str) ||
    isFSType(str) ||
    isPlayerObjectType(str) ||
    isCampaignObjectType(str) ||
    isMiscDataType(str);
}

export const TICKET_TYPE_VALUES = ["Bug","Suggestion","Comment"] as const;
export type TicketType = typeof TICKET_TYPE_VALUES[number];
export const isTicketType = makeTypeGuard(TICKET_TYPE_VALUES);

export const COST_OVERRIDE_TYPE_VALUES = [
  "Don't Override",
  "At Will",
  "At Will or Slot Upcast",
  "Only Special Resource",
  "Or Special Resource",
  "And Special Resource"
] as const;
export type CostOverrideType = typeof COST_OVERRIDE_TYPE_VALUES[number];
export const isCostOverrideType = makeTypeGuard(COST_OVERRIDE_TYPE_VALUES);

export const PROFICIENCY_TYPE_VALUES = ["Skill","Tool","Saving Throw","Special","Armor","Weapon","Special Weapon"] as const;
export type ProficiencyType = typeof PROFICIENCY_TYPE_VALUES[number];
export const isProficiencyType = makeTypeGuard(PROFICIENCY_TYPE_VALUES);
